import { useEffect, useState, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  MedicationGuideContent,
  PrescriptionTabContent,
} from '../../../../presentation/dashboard/patient/PatientDetailPanel';
import { usePatientProfile } from '../../../../presentation/hooks/patient/usePatientProfile';
import { usePosPatientProfile } from '../../../../presentation/hooks/patient/usePosPatientProfile';
import { useLastPrescription } from '../../../../presentation/hooks/prescription/useLastPrescription';
import { useUserPrescriptions } from '../../../../presentation/hooks/prescription/useUserPrescriptions';
import { useMedicationGuide } from '../../../../presentation/hooks/prescription/useMedicationGuide';
import { usePharmInsightGuide } from '../../../../presentation/hooks/recommendation/usePharmInsightGuide';
import useFavoritesStore from '../../../stores/favoritesStore';
import useAuthStore from '../../../stores/authStore';
import LogUtil from '../../../utils/LogUtil';
import { guideToReco } from './guideToReco';

const isResultOk = res => res?.result_code === '200' || res?.resultCode === 200;
const GENDER_MAP = { M: '남', F: '여' };

const formatTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const period = h < 12 ? '오전' : '오후';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${period} ${String(hh).padStart(2, '0')}:${m}`;
};

const formatDate = iso => {
  if (!iso) return '';
  // "2026-04-26" or full ISO
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * PosPatientModal
 *
 * - 트리거: ① POS 환자 자동 감지 (MainLayout) ② 홈 환자 리스트 클릭 (HomePage)
 * - Figma 392:1435 좌/우 분할
 * - 좌패널은 모달 전용 인라인 구성 (DataUser + 알레르기 + AI 추천)
 * - 우패널은 환자관리탭 sub-components 공유 (MedicationGuideContent / PrescriptionTabContent)
 */
const PosPatientModal = ({ patient, onClose }) => {
  const userId = patient?.user_id ?? patient?.id;

  const [activeTab, setActiveTab] = useState('guide');

  const favoriteUserIds = useFavoritesStore(s => s.userIds);
  const isFavorite = userId != null && favoriteUserIds.includes(userId);

  // 데이터 fetch는 hook으로
  const detailQuery = usePatientProfile(userId);
  const lastRxQuery = useLastPrescription(userId);
  const prescriptionListQuery = useUserPrescriptions(userId, { limit: 20 });
  // 앱 미가입 POS 환자: user_id 없음 → 가회원 상세(pos_patient_id, 전화번호 있는 처방만, 서버 반영 1분 지연)
  const posPatientId = userId == null ? patient?.pos_patient_id ?? null : null;
  const posDetailQuery = usePosPatientProfile(posPatientId);
  const brn = useAuthStore(s => s.pharmacy?.business_registration_number);

  const detail = detailQuery.data ?? posDetailQuery.data ?? null;
  // 팝업을 띄운 '방금 들어온 POS 처방' — 환자 줄(/pharm-dashboard/patients)에 병원·진단·약이 같이 온다
  const currentRx = useMemo(() => (
    patient?.medications?.length || patient?.hospital_name
      ? {
          hospital_name: patient.hospital_name,
          doctor_name: patient.doctor_name,
          disease_code: patient.disease_code,
          medications: patient.medications,
          created_at: patient.visit_time,
        }
      : null
  ), [patient]);
  // 처방 목록: /prescriptions/?user_id 는 앱 처방만 온다(POS 없음) → 비면 가회원 처방 → 방금 처방
  const prescriptionList = useMemo(() => {
    const list = prescriptionListQuery.data ?? [];
    if (list.length) return list;
    const posRxs = (posDetailQuery.data?.prescriptions ?? []).map(rx => ({ ...rx, created_at: rx.received_at }));
    if (posRxs.length) return posRxs;
    return currentRx ? [currentRx] : [];
  }, [prescriptionListQuery.data, posDetailQuery.data, currentRx]);
  const lastRx = currentRx ?? lastRxQuery.data ?? prescriptionList[0] ?? null;

  // 복약지도문·AI 맞춤 추천은 방금 처방의 약 기준 (없으면 처방 목록 전체)
  const ediCodes = useMemo(() => {
    const items = currentRx?.medications?.length ? [currentRx] : prescriptionList;
    const allMeds = items.flatMap(p =>
      p.analysis?.medications || p.medications_resolved || p.medications || []
    );
    return Array.from(new Set(
      allMeds
        .map(m => m.edi_code || m.insurance_code || m.medication_code || m.code)
        .filter(Boolean)
    ));
  }, [currentRx, prescriptionList]);
  const guideQuery = useMedicationGuide(ediCodes);
  // AI 맞춤 추천 — 환자관리 상세와 동일하게 GET /pharminsight/guide (edi+brn)
  const insightQuery = usePharmInsightGuide(ediCodes, brn);

  const guideMeds = guideQuery.data ?? [];
  // 안내 API → Figma 15:6461 모양 (로딩/데이터 없음이면 null → 섹션 숨김)
  const reco = useMemo(() => guideToReco(insightQuery.data), [insightQuery.data]);

  useEffect(() => { if (detailQuery.error) LogUtil.error('[PosPatientModal] detail 실패:', detailQuery.error); }, [detailQuery.error]);
  useEffect(() => { if (lastRxQuery.error) LogUtil.error('[PosPatientModal] 마지막 처방전 실패:', lastRxQuery.error); }, [lastRxQuery.error]);
  useEffect(() => { if (prescriptionListQuery.error) LogUtil.error('[PosPatientModal] 처방전 리스트 실패:', prescriptionListQuery.error); }, [prescriptionListQuery.error]);
  useEffect(() => { if (guideQuery.error) LogUtil.error('[PosPatientModal] 복약지도문 실패:', guideQuery.error); }, [guideQuery.error]);
  useEffect(() => { if (insightQuery.error) LogUtil.error('[PosPatientModal] 팜인사이트 안내 실패:', insightQuery.error); }, [insightQuery.error]);

  const merged = useMemo(
    () => ({ ...(patient || {}), ...(detail || {}) }),
    [patient, detail]
  );

  const name = merged.name || merged.patient_name || '환자';
  const gender = GENDER_MAP[merged.gender] || merged.gender || '-';
  const age = merged.age != null ? `${merged.age}세` : '-';
  const visitCount =
    merged.visit_count != null ? `내원 ${merged.visit_count}회` : null;
  const points =
    merged.points != null ? `${Number(merged.points).toLocaleString()}P` : null;
  const couponCount =
    merged.coupons?.available != null
      ? `쿠폰 ${merged.coupons.available}장`
      : merged.coupon_count != null
        ? `쿠폰 ${merged.coupon_count}장`
        : null;
  const visitTime = formatTime(merged.visit_time || merged.created_at);
  const allergyInfo = merged.allergy_info;
  const hospital = lastRx?.hospital_name || '-';
  const diseaseCode = lastRx?.disease_code;
  const diseaseName = lastRx?.disease_name;
  const diagnosis = diseaseName
    ? `${diseaseName}${diseaseCode ? ` (${diseaseCode})` : ''}`
    : diseaseCode || '-';
  const issueDate = formatDate(lastRx?.issue_date || lastRx?.created_at);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.3)' }}
      onClick={onClose}
    >
      <div
        className="flex bg-white overflow-hidden"
        style={{ width: 1014, height: 693, borderRadius: 12 }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── 좌패널 — Figma 15:6456 v2 (450, bg-white, p-24, gap-24) ── */}
        <aside
          className="flex flex-col gap-6 overflow-y-auto"
          style={{
            width: 450,
            background: '#fff',
            borderRight: '1px solid #e4e4e7',
            boxShadow: '1px 0px 17px 0px rgba(68,87,133,0.11)',
            padding: 24,
          }}
        >
          {/* patient 헤더 — Figma 76:17508 (아바타 없음, 이름 SB20 + 별) */}
          <div className="flex flex-col gap-1 w-full" style={{ paddingBottom: 6 }}>
            {/* Name row */}
            <div className="flex gap-3 items-center w-full">
              <div className="flex flex-1 gap-1 items-center min-w-0">
                <span
                  className="truncate"
                  style={{
                    fontSize: 'var(--text-20)',
                    fontWeight: 600,
                    color: '#18181b',
                    lineHeight: 1.35,
                    letterSpacing: '-0.4px',
                  }}
                >
                  {name}
                </span>
                {(isFavorite || merged.is_favorite === true) && (
                  <img
                    src="/images/icons/star-fill.svg"
                    alt="즐겨찾기"
                    style={{ width: 18, height: 18 }}
                    className="shrink-0"
                  />
                )}
              </div>
              <div
                className="flex gap-0.5 items-center shrink-0 whitespace-nowrap"
                style={{
                  fontSize: 'var(--text-14)',
                  fontWeight: 500,
                  color: '#71717a',
                  lineHeight: '20px',
                  letterSpacing: '-0.28px',
                }}
              >
                <span>
                  {/* 접수 경로는 프로필이 아닌 방문 행(patient) 기준 — 백엔드 pos_serial(앱 접수는 null)/visit_channel */}
                  {patient?.pos_serial || patient?.visit_channel === 'walk-in'
                    ? 'POS 접수'
                    : '앱으로 접수'}
                </span>
                {visitTime && (
                  <>
                    <span>·</span>
                    <span>{visitTime}</span>
                  </>
                )}
              </div>
            </div>
            {/* Metadata row */}
            <div
              className="flex items-start w-full"
              style={{
                fontSize: 'var(--text-14)',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '-0.28px',
              }}
            >
              <div
                className="flex flex-1 gap-0.5 items-start min-w-0 whitespace-nowrap"
                style={{ color: '#52525b' }}
              >
                <span>{gender}</span>
                <span style={{ color: '#bcbcc2' }}>·</span>
                <span>{age}</span>
              </div>
              <div
                className="flex gap-3 items-center shrink-0 whitespace-nowrap"
                style={{ color: '#71717a' }}
              >
                {visitCount && <span>{visitCount}</span>}
                {visitCount && (points || couponCount) && (
                  <span style={{ color: '#e4e4e7' }}>|</span>
                )}
                {points && <span>{points}</span>}
                {points && couponCount && (
                  <span style={{ color: '#e4e4e7' }}>|</span>
                )}
                {couponCount && <span>{couponCount}</span>}
              </div>
            </div>
          </div>

          {/* condition — Figma 15:6458: hospital 헤더 + 진단명/처방일 라벨-데이터 행 + 알레르기 */}
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-3 w-full">
              <p
                style={{
                  fontSize: 'var(--text-14)',
                  fontWeight: 500,
                  color: '#27272a',
                  lineHeight: '20px',
                  letterSpacing: '-0.28px',
                }}
              >
                {hospital}
              </p>
              <div className="flex flex-wrap w-full">
                {[
                  { label: '진단명', value: diagnosis },
                  { label: '처방일', value: issueDate },
                ].map(it => (
                  <div
                    key={it.label}
                    className="flex items-center flex-1"
                    style={{
                      minWidth: 220,
                      height: 28,
                      background: '#fff',
                      borderTop: '1px solid #e4e4e7',
                      borderBottom: '1px solid #e4e4e7',
                    }}
                  >
                    <div
                      className="flex items-center h-full shrink-0"
                      style={{
                        minWidth: 56,
                        padding: '0 8px',
                        background: '#f4f4f5',
                      }}
                    >
                      <span
                        className="whitespace-nowrap"
                        style={{
                          fontSize: 'var(--text-14)',
                          color: '#71717a',
                          lineHeight: '20px',
                          letterSpacing: '-0.28px',
                        }}
                      >
                        {it.label}
                      </span>
                    </div>
                    <div
                      className="flex items-center flex-1 h-full min-w-0"
                      style={{ padding: '0 8px' }}
                    >
                      <span
                        className="truncate"
                        style={{
                          fontSize: 'var(--text-14)',
                          color: '#3f3f46',
                          lineHeight: '20px',
                          letterSpacing: '-0.28px',
                        }}
                      >
                        {it.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 알레르기 alert — Figma 69:37043 (bg #fef4f5, 제목 #27272a, 본문 #52525b) */}
            {allergyInfo && allergyInfo !== '없음' && (
              <div
                className="flex gap-1 items-start w-full"
                style={{
                  background: '#fef4f5',
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <img
                  src="/images/icons/alert-fill.svg"
                  alt=""
                  style={{ width: 16, height: 16 }}
                  className="shrink-0"
                />
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <p
                    style={{
                      fontSize: 'var(--text-12)',
                      fontWeight: 600,
                      color: '#27272a',
                      lineHeight: '18px',
                    }}
                  >
                    알레르기·부작용 이력
                  </p>
                  <p
                    style={{
                      fontSize: 'var(--text-12)',
                      fontWeight: 400,
                      color: '#52525b',
                      lineHeight: '18px',
                    }}
                  >
                    {allergyInfo}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* AI 맞춤 추천 — Figma 15:6461 v2 (카드 래퍼 없음, 패널 위 직접) */}
          {reco &&
            (reco.recommendation_reason ||
              (reco.products && reco.products.length > 0)) && (
              <div className="flex flex-col gap-4 w-full">
                {/* 헤더 + 추천 사유 — Figma 15:651 */}
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex gap-1 items-center">
                    <img
                      src="/images/icons/ai-fill.svg"
                      alt=""
                      style={{ width: 16, height: 16 }}
                      className="shrink-0"
                    />
                    <span
                      style={{
                        fontSize: 'var(--text-16)',
                        fontWeight: 600,
                        color: '#1d356d',
                        lineHeight: '22px',
                        letterSpacing: '-0.32px',
                      }}
                    >
                      AI 맞춤 추천
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 'var(--text-14)',
                      fontWeight: 400,
                      color: '#3f3f46',
                      lineHeight: '22px',
                      letterSpacing: '-0.28px',
                    }}
                  >
                    {reco.recommendation_reason || ''}
                  </p>
                </div>

                {/* 구매율 — Figma 69:16559 (M12, bar bg #efeff1) */}
                {reco.purchase_rate &&
                  (() => {
                    const stats = reco.purchase_rate
                      .split('·')
                      .map(s => s.trim())
                      .filter(Boolean);
                    const first = stats[0] || '';
                    const m = first.match(/^(.+?)\s*(\d+)%\s*$/);
                    const label = m ? m[1] : first;
                    const pct = m ? parseInt(m[2], 10) : null;
                    return (
                      <div className="flex flex-col gap-1 items-end w-full">
                        <div
                          className="flex items-center justify-between w-full whitespace-nowrap"
                          style={{
                            fontSize: 'var(--text-12)',
                            fontWeight: 500,
                            color: '#71717a',
                            lineHeight: '18px',
                          }}
                        >
                          <span>{label}</span>
                          {pct !== null && <span>{pct}%</span>}
                        </div>
                        {pct !== null && (
                          <div
                            className="w-full"
                            style={{
                              height: 8,
                              background: '#efeff1',
                              borderRadius: 4,
                            }}
                          >
                            <div
                              style={{
                                height: 8,
                                width: `${pct}%`,
                                background: '#1d356d',
                                borderRadius: 4,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {/* 3 product cards — Figma 15:6468 (flex-1, 정방형 이미지 + 좌상단 순번 태그) */}
                <div className="flex gap-3 items-start w-full">
                  {(reco.products || []).slice(0, 3).map((p, idx) => (
                    <div
                      key={p.rank ?? idx}
                      className="flex flex-col flex-1 min-w-0"
                      style={{ background: '#fff', borderRadius: 4 }}
                    >
                      <div className="flex flex-col gap-2 p-1 w-full">
                        <div
                          className="relative w-full overflow-hidden"
                          style={{
                            aspectRatio: '1 / 1',
                            borderRadius: 4,
                            background: '#f4f4f5',
                          }}
                        >
                          {p.image_url && (
                            <img
                              src={p.image_url}
                              alt={p.name}
                              className="absolute inset-0"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={e => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div
                            className="absolute flex items-center justify-center"
                            style={{
                              top: 0,
                              left: 0,
                              width: 20,
                              background: '#1d356d',
                              borderBottomRightRadius: 4,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 'var(--text-14)',
                                fontWeight: 600,
                                color: '#fff',
                                lineHeight: '20px',
                                letterSpacing: '-0.28px',
                              }}
                            >
                              {p.rank ?? idx + 1}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 w-full">
                          <span
                            style={{
                              fontSize: 'var(--text-12)',
                              fontWeight: 500,
                              color: '#18181b',
                              lineHeight: '18px',
                            }}
                          >
                            {p.name}
                          </span>
                          <span
                            style={{
                              fontSize: 'var(--text-12)',
                              fontWeight: 400,
                              color: '#71717a',
                              lineHeight: '18px',
                            }}
                          >
                            {p.manufacturer || p.maker}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </aside>

        {/* ── 우패널 ── */}
        <main className="flex flex-col flex-1 min-w-0">
          <header
            className="flex items-end justify-between"
            style={{
              background: '#fff',
              borderBottom: '1px solid #e4e4e7',
              boxShadow: '0px 4px 5px rgba(186,186,186,0.2)',
            }}
          >
            <div className="flex items-end gap-6 px-6">
              <button
                type="button"
                onClick={() => setActiveTab('guide')}
                className="flex items-center gap-2 py-1.5"
                style={{
                  borderBottom:
                    activeTab === 'guide'
                      ? '2px solid #1d356d'
                      : '2px solid transparent',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-16)',
                    fontWeight: 600, // Figma 15:6472 menu__item — 활성/비활성 모두 SemiBold
                    color: activeTab === 'guide' ? '#1d356d' : '#a1a1aa',
                    lineHeight: '22px',
                    letterSpacing: '-0.32px',
                  }}
                >
                  복약지도문
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('prescription')}
                className="flex items-center gap-2 py-1.5"
                style={{
                  borderBottom:
                    activeTab === 'prescription'
                      ? '2px solid #1d356d'
                      : '2px solid transparent',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-16)',
                    fontWeight: 600, // Figma 15:6472 menu__item — 활성/비활성 모두 SemiBold
                    color: activeTab === 'prescription' ? '#1d356d' : '#a1a1aa',
                    lineHeight: '22px',
                    letterSpacing: '-0.32px',
                  }}
                >
                  처방전
                </span>
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="닫기"
              className="flex items-center justify-center hover:bg-zinc-50"
              style={{ width: 60, height: 58 }}
            >
              <X size={24} className="text-[#3f3f46]" strokeWidth={1.75} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto" style={{ padding: 24 }}>
            {activeTab === 'guide' && (
              <MedicationGuideContent guideMeds={guideMeds} />
            )}
            {activeTab === 'prescription' && (
              <PrescriptionTabContent
                prescriptions={prescriptionList}
                merged={merged}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PosPatientModal;
